import { useQuery } from '@tanstack/react-query';
import {
  TrendingUp,
  Baby,
  Heart,
  Users,
  Star,
  BarChart3,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';

const supabase = getSupabaseAdminClient();

export function NameStatsPage() {
  // Fetch overall stats
  const { data: overallStats, isLoading: loadingOverall } = useQuery({
    queryKey: ['names-overall-stats'],
    queryFn: async () => {
      const [totalResult, activeResult, maleResult, femaleResult, unisexResult] = await Promise.all([
        supabase.from('baby_names').select('id', { count: 'exact', head: true }),
        supabase.from('baby_names').select('id', { count: 'exact', head: true }).eq('is_active', true),
        supabase.from('baby_names').select('id', { count: 'exact', head: true }).eq('gender', 'male'),
        supabase.from('baby_names').select('id', { count: 'exact', head: true }).eq('gender', 'female'),
        supabase.from('baby_names').select('id', { count: 'exact', head: true }).eq('gender', 'unisex'),
      ]);

      return {
        total: totalResult.count || 0,
        active: activeResult.count || 0,
        male: maleResult.count || 0,
        female: femaleResult.count || 0,
        unisex: unisexResult.count || 0,
      };
    },
  });

  // Fetch favorite counts (most favorited names)
  const { data: topFavorites, isLoading: loadingFavorites } = useQuery({
    queryKey: ['names-top-favorites'],
    queryFn: async () => {
      // Get favorite counts per name from name_swipes with swipe_type = 'favorite'
      const { data, error } = await supabase
        .from('name_swipes')
        .select('name_id, baby_names:baby_names!name_swipes_name_id_fkey(name, gender)')
        .eq('swipe_type', 'favorite')
        .limit(1000);

      if (error) throw error;

      // Count favorites per name
      const countMap: Record<string, { name: string; gender: string; count: number }> = {};
      (data as { name_id: string; baby_names: { name: string; gender: string } | null }[])?.forEach(fav => {
        if (fav.baby_names) {
          if (!countMap[fav.name_id]) {
            countMap[fav.name_id] = {
              name: fav.baby_names.name,
              gender: fav.baby_names.gender,
              count: 0,
            };
          }
          countMap[fav.name_id].count++;
        }
      });

      // Sort by count and take top 10
      return Object.values(countMap)
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);
    },
  });

  // Fetch swipe statistics
  const { data: swipeStats, isLoading: loadingSwipes } = useQuery({
    queryKey: ['names-swipe-stats'],
    queryFn: async () => {
      const [totalSwipes, likes, dislikes, favorites] = await Promise.all([
        supabase.from('name_swipes').select('id', { count: 'exact', head: true }),
        supabase.from('name_swipes').select('id', { count: 'exact', head: true }).eq('swipe_type', 'like'),
        supabase.from('name_swipes').select('id', { count: 'exact', head: true }).eq('swipe_type', 'dislike'),
        supabase.from('name_swipes').select('id', { count: 'exact', head: true }).eq('swipe_type', 'favorite'),
      ]);

      return {
        total: totalSwipes.count || 0,
        likes: (likes.count || 0) + (favorites.count || 0), // likes + favorites count as positive
        dislikes: dislikes.count || 0,
      };
    },
  });

  // Fetch origin distribution
  const { data: originStats, isLoading: loadingOrigins } = useQuery({
    queryKey: ['names-origin-stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('baby_names')
        .select('origin')
        .not('origin', 'is', null);

      if (error) throw error;

      // Count by origin
      const countMap: Record<string, number> = {};
      (data as { origin: string }[])?.forEach(item => {
        const origin = item.origin || 'Unbekannt';
        countMap[origin] = (countMap[origin] || 0) + 1;
      });

      // Sort by count and take top 10
      return Object.entries(countMap)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10)
        .map(([origin, count]) => ({ origin, count }));
    },
  });

  const genderData = overallStats ? [
    { name: 'Männlich', value: overallStats.male, color: '#3b82f6' },
    { name: 'Weiblich', value: overallStats.female, color: '#f43f5e' },
    { name: 'Unisex', value: overallStats.unisex, color: '#8b5cf6' },
  ] : [];

  const isLoading = loadingOverall || loadingFavorites || loadingSwipes || loadingOrigins;

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Namen-Statistiken</h1>
        <p className="text-slate-600">Beliebte Namen und Trends</p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-slate-500">Lädt Statistiken...</div>
        </div>
      ) : (
        <>
          {/* Overview Stats */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <Baby className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <div className="text-xl font-bold text-slate-900">
                    {overallStats?.total.toLocaleString('de-DE')}
                  </div>
                  <div className="text-sm text-slate-600">Gesamt</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="text-xl font-bold text-slate-900">
                    {overallStats?.male.toLocaleString('de-DE')}
                  </div>
                  <div className="text-sm text-slate-600">Männlich</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center">
                  <Heart className="w-5 h-5 text-rose-600" />
                </div>
                <div>
                  <div className="text-xl font-bold text-slate-900">
                    {overallStats?.female.toLocaleString('de-DE')}
                  </div>
                  <div className="text-sm text-slate-600">Weiblich</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Star className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <div className="text-xl font-bold text-slate-900">
                    {overallStats?.unisex.toLocaleString('de-DE')}
                  </div>
                  <div className="text-sm text-slate-600">Unisex</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <div className="text-xl font-bold text-slate-900">
                    {swipeStats?.total.toLocaleString('de-DE')}
                  </div>
                  <div className="text-sm text-slate-600">Swipes gesamt</div>
                </div>
              </div>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Gender Distribution */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Geschlechterverteilung</h2>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={genderData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {genderData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: number) => value.toLocaleString('de-DE')} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Swipe Stats */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Swipe-Statistiken</h2>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Gefällt', value: swipeStats?.likes || 0 },
                        { name: 'Gefällt nicht', value: swipeStats?.dislikes || 0 },
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    >
                      <Cell fill="#10b981" />
                      <Cell fill="#ef4444" />
                    </Pie>
                    <Tooltip formatter={(value: number) => value.toLocaleString('de-DE')} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Bottom Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Favorites */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <Star className="w-5 h-5 text-amber-500" />
                <h2 className="text-lg font-semibold text-slate-900">Top 10 Favoriten</h2>
              </div>
              {topFavorites && topFavorites.length > 0 ? (
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={topFavorites}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 60, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="name" width={80} />
                      <Tooltip formatter={(value: number) => value.toLocaleString('de-DE')} />
                      <Bar dataKey="count" fill="#0d9488" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-slate-500">
                  Noch keine Favoriten vorhanden
                </div>
              )}
            </div>

            {/* Origin Distribution */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-teal-500" />
                <h2 className="text-lg font-semibold text-slate-900">Top 10 Herkunftsländer</h2>
              </div>
              {originStats && originStats.length > 0 ? (
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={originStats}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="origin" width={100} />
                      <Tooltip formatter={(value: number) => value.toLocaleString('de-DE')} />
                      <Bar dataKey="count" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-slate-500">
                  Keine Herkunftsdaten vorhanden
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
